import type { CSSProperties, FC, ReactNode } from 'react'
import type { DerivedTheme, Scheme } from '@/components/landing/theme-preview'

import { useMemo, useState } from 'react'
import { Check, Copy, Pipette } from 'lucide-react'
import { cn } from '@/lib/utils'
import { bestInk, contrast, ensureContrast } from '@/lib/color'
import ThemePreview from '@/components/landing/theme-preview'
import SectionHeading, { Accent } from '@/components/landing/section-heading'

const ACCENTS = [
    { name: 'Ember', value: '#ff5b1f' },
    { name: 'Volt', value: '#c6f432' },
    { name: 'Cobalt', value: '#3b6cff' },
    { name: 'Orchid', value: '#b46cff' },
    { name: 'Jade', value: '#19c37d' },
    { name: 'Gold', value: '#f2b33d' },
    { name: 'Graphite', value: '#1a1a1a' }
]

const FONTS = [
    { id: 'sans', label: 'Sans', name: 'Instrument Sans', css: 'var(--font-app-sans)' },
    { id: 'serif', label: 'Serif', name: 'Instrument Serif', css: 'var(--font-app-serif)' },
    { id: 'mono', label: 'Mono', name: 'Martian Mono', css: 'var(--font-app-mono)' }
]

const SURFACES: Record<Scheme, { surface: string, panel: string, line: string, text: string, muted: string }> = {
    dark: { surface: '#131316', panel: '#1d1d22', line: 'rgba(255, 255, 255, 0.08)', text: '#f4f2ee', muted: '#9a9893' },
    light: { surface: '#ffffff', panel: '#f1efea', line: 'rgba(0, 0, 0, 0.08)', text: '#141416', muted: '#5f5d58' }
}

const STAGE = '#0b0b0d'

/** The same idea as the real element: read a few tokens, derive the rest, and keep every pair readable. */
const deriveTheme = (accent: string, scheme: Scheme): DerivedTheme => {
    const base = SURFACES[scheme]
    const usable = ensureContrast(accent, base.surface, 3)
    const accentInk = bestInk(usable.color)

    return {
        scheme,
        ...base,
        accent: usable.color,
        accentInk,
        adjusted: usable.adjusted,
        stageAccent: ensureContrast(accent, STAGE, 3).color,
        ratios: {
            text: contrast(base.text, base.surface),
            muted: contrast(base.muted, base.surface),
            ink: contrast(accentInk, usable.color)
        }
    }
}

interface ControlProps {
    label: string
    value?: string
    children: ReactNode
}

const Control: FC<ControlProps> = ({ label, value, children }) => {
    return (
        <div>
            <div className='flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-ink/55'>
                <span>{label}</span>
                {value && <span className='text-ink'>{value}</span>}
            </div>
            <div className='mt-3'>
                {children}
            </div>
        </div>
    )
}

interface SegmentedProps<T extends string> {
    label: string
    options: { id: T, label: string }[]
    value: T
    onChange: (value: T) => void
}

function Segmented<T extends string>({ label, options, value, onChange }: SegmentedProps<T>) {
    return (
        <div role='radiogroup' aria-label={label} className='grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-[#efece5] p-1'>
            {options.map(option => (
                <button
                    key={option.id}
                    type='button'
                    role='radio'
                    aria-checked={value === option.id}
                    onClick={() => onChange(option.id)}
                    className={cn(
                        'h-9 rounded-lg text-sm transition-all',
                        value === option.id ? 'bg-white text-ink shadow-[0_2px_10px_-4px_rgba(0,0,0,0.25)]' : 'text-ink/55 hover:text-ink'
                    )}
                >
                    {option.label}
                </button>
            ))}
        </div>
    )
}

interface DerivedRowProps {
    name: string
    value: string
    ratio: number
    minimum: number
}

const DerivedRow: FC<DerivedRowProps> = ({ name, value, ratio, minimum }) => {
    const passes = ratio >= minimum

    return (
        <li className='flex items-center justify-between gap-3'>
            <span className='flex min-w-0 items-center gap-2'>
                <span className='size-3 shrink-0 rounded-[4px] ring-1 ring-inset ring-white/15' style={{ background: value }} />
                <span className='truncate text-bone/85'>{name}</span>
            </span>
            <span className='flex shrink-0 items-center gap-2 tabular-nums'>
                <span className='text-steel'>{ratio.toFixed(1)}:1</span>
                <span className={cn('flex items-center gap-1', passes ? 'text-mint' : 'text-danger')}>
                    <Check aria-hidden='true' className='size-3.5' />
                    {minimum}:1
                </span>
            </span>
        </li>
    )
}

const rangeStyle = (value: number, min: number, max: number): CSSProperties => ({
    '--range-progress': `${((value - min) / (max - min)) * 100}%`,
    '--range-rest': 'rgba(8, 8, 10, 0.14)'
} as CSSProperties)

const ThemingSection: FC = () => {
    const [accent, setAccent] = useState(ACCENTS[0].value)
    const [scheme, setScheme] = useState<Scheme>('dark')
    const [radius, setRadius] = useState(18)
    const [roundness, setRoundness] = useState(0.6)
    const [fontId, setFontId] = useState('sans')
    const [copied, setCopied] = useState(false)

    const theme = useMemo(() => deriveTheme(accent, scheme), [accent, scheme])
    const font = FONTS.find(option => option.id === fontId) ?? FONTS[0]

    const css = [
        'og-game {',
        `  --og-accent: ${accent};`,
        `  --og-color-scheme: ${scheme};`,
        `  --og-radius: ${radius}px;`,
        `  --og-roundness: ${roundness.toFixed(2)};`,
        `  --og-font: "${font.name}";`,
        '}'
    ]

    const copyCss = async () => {
        try {
            await navigator.clipboard.writeText(css.join('\n'))
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1600)
        }
        catch {
            setCopied(false)
        }
    }

    return (
        <section id='theming' aria-labelledby='theming-title' className='relative bg-bone py-24 text-ink md:py-32'>
            <div className='mx-auto max-w-[1320px] px-5 md:px-8'>
                <SectionHeading
                    id='theming-title'
                    tone='light'
                    index='02'
                    label='Theming'
                    title={<>You set the colours, the fonts and the <Accent>corners</Accent> of every game.</>}
                    lead='The games read your accent, colour scheme, fonts and corner radius from CSS tokens and derive every other colour from them with contrast checks. In the dashboard, you enter your domain and accept a theme taken from your own site, or adjust it.'
                />

                <div className='mt-14 grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]'>
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-col gap-7 rounded-[28px] border border-black/[0.06] bg-white p-6 shadow-[0_30px_80px_-50px_rgba(0,0,0,0.35)]'>
                            <Control label='Accent' value={accent.toUpperCase()}>
                                <div className='flex flex-wrap gap-2'>
                                    {ACCENTS.map(option => (
                                        <button
                                            key={option.value}
                                            type='button'
                                            onClick={() => setAccent(option.value)}
                                            aria-pressed={accent === option.value}
                                            title={option.name}
                                            className={cn(
                                                'size-9 rounded-full ring-offset-2 ring-offset-white transition-transform',
                                                accent === option.value ? 'ring-2 ring-ink' : 'ring-1 ring-black/10 hover:scale-105'
                                            )}
                                            style={{ background: option.value }}
                                        >
                                            <span className='sr-only'>{option.name}</span>
                                        </button>
                                    ))}

                                    <label
                                        title='Pick any colour'
                                        className='relative grid size-9 cursor-pointer place-items-center rounded-full bg-[conic-gradient(from_0deg,#ff5b1f,#f2b33d,#c6f432,#19c37d,#3b6cff,#b46cff,#ff5b1f)] ring-1 ring-black/10 transition-transform hover:scale-105'
                                    >
                                        <Pipette aria-hidden='true' className='size-4 text-white drop-shadow' />
                                        <input
                                            type='color'
                                            value={accent}
                                            onChange={event => setAccent(event.target.value)}
                                            className='absolute inset-0 cursor-pointer opacity-0'
                                            aria-label='Pick any accent colour'
                                        />
                                    </label>
                                </div>
                            </Control>

                            <Control label='Colour scheme'>
                                <Segmented
                                    label='Colour scheme'
                                    options={[{ id: 'dark', label: 'Dark' }, { id: 'light', label: 'Light' }]}
                                    value={scheme}
                                    onChange={setScheme}
                                />
                            </Control>

                            <Control label='Corner radius' value={`${radius}px`}>
                                <input
                                    type='range'
                                    min={0}
                                    max={28}
                                    value={radius}
                                    onChange={event => setRadius(Number(event.target.value))}
                                    aria-label='Corner radius'
                                    className='range-ember w-full'
                                    style={rangeStyle(radius, 0, 28)}
                                />
                            </Control>

                            <Control label='Control roundness' value={roundness.toFixed(2)}>
                                <input
                                    type='range'
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={roundness}
                                    onChange={event => setRoundness(Number(event.target.value))}
                                    aria-label='Control roundness'
                                    className='range-ember w-full'
                                    style={rangeStyle(roundness, 0, 1)}
                                />
                            </Control>

                            <Control label='Typeface'>
                                <Segmented
                                    label='Typeface'
                                    options={FONTS.map(option => ({ id: option.id, label: option.label }))}
                                    value={fontId}
                                    onChange={setFontId}
                                />
                            </Control>
                        </div>

                        <div className='rounded-[28px] bg-ink p-5 font-mono text-[11.5px] text-bone/80'>
                            <div className='flex items-center justify-between'>
                                <span className='text-[10px] uppercase tracking-[0.16em] text-steel'>your-site.css</span>
                                <button
                                    type='button'
                                    onClick={copyCss}
                                    className='inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-steel transition-colors hover:bg-white/[0.06] hover:text-bone'
                                >
                                    {copied ? <Check aria-hidden='true' className='size-3.5 text-mint' /> : <Copy aria-hidden='true' className='size-3.5' />}
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>

                            <pre className='mt-3 overflow-x-auto leading-[1.85]'>
                                <code>
                                    {css.map((line, index) => {
                                        const match = line.match(/^(\s*)(--[\w-]+):\s(.+);$/)

                                        if (!match) {
                                            return (
                                                <span key={index} className='block text-[#7fe0b5]'>
                                                    {line}
                                                </span>
                                            )
                                        }

                                        return (
                                            <span key={index} className='block'>
                                                {match[1]}
                                                <span className='text-bone/70'>{match[2]}</span>
                                                <span className='text-steel'>: </span>
                                                <span className='text-[#ffb08a]'>{match[3]}</span>
                                                <span className='text-steel'>;</span>
                                            </span>
                                        )
                                    })}
                                </code>
                            </pre>

                            <div className='mt-4 border-t border-white/10 pt-4'>
                                <div className='text-[10px] uppercase tracking-[0.16em] text-steel'>Derived, with contrast checks</div>

                                <ul className='mt-3 grid gap-2.5'>
                                    <DerivedRow name='--og-text' value={theme.text} ratio={theme.ratios.text} minimum={7} />
                                    <DerivedRow name='--og-muted' value={theme.muted} ratio={theme.ratios.muted} minimum={4.5} />
                                    <DerivedRow name='--og-accent-ink' value={theme.accentInk} ratio={theme.ratios.ink} minimum={4.5} />
                                </ul>

                                <p className={cn('mt-3 text-[11px] leading-relaxed', theme.adjusted ? 'text-ember-soft' : 'text-steel')}>
                                    {theme.adjusted
                                        ? `Your accent was too faint on this scheme, so it was moved to ${theme.accent.toUpperCase()}. Same hue, readable.`
                                        : 'Your accent reads well on this scheme as it is.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='relative flex items-center justify-center overflow-hidden rounded-[28px] border border-black/[0.06] bg-[#e7e3da] p-4 sm:p-8 lg:p-12'>
                        <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(8,8,10,0.09)_1px,transparent_1px)] [background-size:16px_16px]' />
                        <div aria-hidden='true' className='pointer-events-none absolute -top-24 left-1/2 h-72 w-[70%] -translate-x-1/2 rounded-full blur-3xl transition-colors duration-500' style={{ background: `${theme.accent}33` }} />

                        <div className='relative w-full max-w-[680px]'>
                            <div className='mb-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-ink/55'>
                                <span>Live preview</span>
                                <span>Press bet</span>
                            </div>

                            <ThemePreview theme={theme} radius={radius} roundness={roundness} font={font.css} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ThemingSection
